// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 7a6a7dc8-6599-45ab-8912-eb2351f24722

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, LinearProgress, CircularProgress, Tooltip, Divider,
  InputAdornment,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import HelpBubble from './HelpBubble';

// Secure document exchange between Optimum and a client, replacing email.
//
// Every document carries a SCOPE, and the scope is the security boundary:
//   BASE   -> Optimum-owned reference material, reusable across every client
//   CLIENT -> tenant-locked; only that client (and Optimum master) ever sees it
//
// The API re-checks scope and tenant on every call and the database enforces
// row-level security on attachment.tenant_id — this UI is convenience, not the
// security boundary. Only Optimum master users may create BASE documents.
//
// Uploads never pass through this app's servers: we ask the API for a presigned
// URL and PUT the bytes straight into encrypted, tenant-partitioned storage.
// SharePoint / Confluence documents are LINKED, never copied.

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const SOURCE_LABEL = { s3: 'S3 upload', sharepoint: 'SharePoint', confluence: 'Confluence' };

const fmtSize = (n) => {
  const b = Number(n);
  if (!b || Number.isNaN(b)) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleDateString();
};

function ScopeChip({ scope }) {
  const isBase = scope === 'base';
  return (
    <Tooltip title={isBase
      ? 'BASE — Optimum-owned reference material, reusable across clients'
      : 'CLIENT — locked to this tenant; no other client can ever see it'}>
      <Chip
        size="small"
        label={isBase ? 'BASE' : 'CLIENT'}
        sx={{
          height: 20, fontWeight: 700, fontSize: 11, letterSpacing: 0.4,
          bgcolor: isBase ? BLUE : '#E3E8ED',
          color: isBase ? '#fff' : GREY,
        }}
      />
    </Tooltip>
  );
}

function SourceChip({ source }) {
  const linked = source === 'sharepoint' || source === 'confluence';
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={linked ? <LinkIcon sx={{ fontSize: 14 }} /> : undefined}
      label={SOURCE_LABEL[source] || source || 'unknown'}
      sx={{ height: 20, fontSize: 11, color: GREY, borderColor: '#cfd8dc' }}
    />
  );
}

// PUT the file straight to the presigned URL, reporting progress as it goes.
function putWithProgress(url, file, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      // A zero-byte file reports lengthComputable with total 0, so this was
      // 0/0 -> NaN and the progress label read "NaN%".
      if (e.lengthComputable && e.total > 0) onPct(Math.round((e.loaded / e.total) * 100));
      else if (e.lengthComputable) onPct(100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`storage rejected the upload (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.send(file);
  });
}

export default function FileExchange({ tenantId, factSheetId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [scopeFilter, setScopeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [text, setText] = useState('');

  const [uploadScope, setUploadScope] = useState('client');
  const [uploads, setUploads] = useState([]); // { key, name, pct, status, error }
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const [linkDialog, setLinkDialog] = useState(null); // { source, title, url }
  const [linkBusy, setLinkBusy] = useState(false);

  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');
  const effectiveTenant = tenantId || (me && me.tenantId) || '';

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const body = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here — before the !res.ok check below could report the real
    // status — so a 502 surfaced as "Unexpected token <". Same guarded form as
    // AiEnrichment and RelationshipWizard.
    let data = {};
    try { data = body ? JSON.parse(body) : {}; } catch (e) { data = {}; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = [`tenantId=${encodeURIComponent(effectiveTenant)}`];
      if (factSheetId) qs.push(`factSheetId=${encodeURIComponent(factSheetId)}`);
      const data = await api(`/documents?${qs.join('&')}`);
      setFiles(Array.isArray(data) ? data : (data.items || []));
      setUnavailable('');
    } catch (e) {
      // The screen must still render: show the empty state and say why.
      setFiles([]);
      setUnavailable('Document storage is not yet deployed for this environment — the file exchange will light up once the documents API and its encrypted bucket are provisioned.');
    } finally {
      setLoading(false);
    }
  }, [api, effectiveTenant, factSheetId]);

  useEffect(() => { load(); }, [load]);

  // Client users can never create BASE documents — keep the control honest even
  // if the session changes underneath us.
  useEffect(() => {
    if (!iAmMaster && uploadScope !== 'client') setUploadScope('client');
  }, [iAmMaster, uploadScope]);

  const visible = useMemo(() => {
    const q = text.trim().toLowerCase();
    return files.filter((f) => {
      if (scopeFilter !== 'all' && (f.scope || 'client') !== scopeFilter) return false;
      if (sourceFilter !== 'all' && f.source !== sourceFilter) return false;
      if (!q) return true;
      return [f.title, f.addedBy || f.added_by, SOURCE_LABEL[f.source], f.url]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [files, scopeFilter, sourceFilter, text]);

  const setUpload = (key, patch) =>
    setUploads((list) => list.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  const uploadOne = async (file, scope) => {
    const key = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setUploads((list) => [...list, { key, name: file.name, pct: 0, status: 'presigning', error: '' }]);
    try {
      const presign = await api('/documents/presign', {
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          scope,
          factSheetId: factSheetId || null,
          tenantId: scope === 'base' ? null : effectiveTenant,
        }),
      });
      const putUrl = presign.uploadUrl || presign.url;
      if (!putUrl) throw new Error('the API did not return an upload URL');

      setUpload(key, { status: 'uploading' });
      await putWithProgress(putUrl, file, (pct) => setUpload(key, { pct }));

      setUpload(key, { status: 'registering', pct: 100 });
      await api('/documents', {
        method: 'POST',
        body: JSON.stringify({
          title: file.name,
          scope,
          source: 's3',
          s3Key: presign.s3Key || presign.key || null,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          factSheetId: factSheetId || null,
          tenantId: scope === 'base' ? null : effectiveTenant,
        }),
      });
      setUpload(key, { status: 'done' });
    } catch (e) {
      setUpload(key, { status: 'error', error: e.message });
    }
  };

  const handleFiles = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setErr(''); setMsg('');
    const scope = iAmMaster ? uploadScope : 'client';
    for (const f of picked) {
      // Sequential: one presign + one PUT at a time keeps progress honest.
      await uploadOne(f, scope);
    }
    await load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer && e.dataTransfer.files);
  };

  const download = async (f) => {
    setErr('');
    try {
      if (f.source === 'sharepoint' || f.source === 'confluence') {
        // Linked, not copied — send the user to the system of record.
        window.open(f.url, '_blank', 'noopener,noreferrer');
        return;
      }
      const data = await api(`/documents/url?id=${encodeURIComponent(f.id)}`);
      const url = data.url || data.downloadUrl;
      if (!url) throw new Error('no download URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e.message);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.title}" from the exchange?${f.source === 's3' ? ' The stored file is deleted.' : ' The linked document itself is untouched.'}`)) return;
    setErr(''); setMsg('');
    try {
      await api(`/documents?id=${encodeURIComponent(f.id)}`, { method: 'DELETE' });
      setMsg(`Removed ${f.title}`);
      await load();
    } catch (e) {
      setErr(e.message);
    }
  };

  const saveLink = async () => {
    if (!linkDialog) return;
    setLinkBusy(true); setErr(''); setMsg('');
    try {
      const scope = iAmMaster ? linkDialog.scope : 'client';
      await api('/documents', {
        method: 'POST',
        body: JSON.stringify({
          title: linkDialog.title.trim(),
          url: linkDialog.url.trim(),
          source: linkDialog.source,
          scope,
          s3Key: null,           // linked, never copied
          contentType: null,
          sizeBytes: null,
          factSheetId: factSheetId || null,
          tenantId: scope === 'base' ? null : effectiveTenant,
        }),
      });
      setMsg(`Linked ${linkDialog.title.trim()}`);
      setLinkDialog(null);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setLinkBusy(false);
    }
  };

  const activeUploads = uploads.filter((u) => u.status !== 'done');

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>File exchange</Typography>
        <HelpBubble topic="fileExchange" />
        <Chip size="small" label={iAmMaster ? 'Optimum master — base + client documents' : 'Your organization only'}
          sx={{ height: 22 }} />
        {factSheetId && <Chip size="small" variant="outlined" label="scoped to this application" sx={{ height: 22 }} />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
        <Button startIcon={<LinkIcon />} variant="outlined"
          onClick={() => setLinkDialog({ source: 'sharepoint', title: '', url: '', scope: iAmMaster ? uploadScope : 'client' })}
          sx={{ color: BLUE, borderColor: BLUE }}>
          Link a document
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: GREY, mb: 2, maxWidth: 900 }}>
        Discovery artifacts move through this portal instead of email. Every document is scoped
        either <b>BASE</b> (Optimum-owned, reusable across clients) or <b>CLIENT</b> (locked to this
        tenant). A client never sees another client&rsquo;s files — the database enforces that with
        row-level security, not just this screen.
      </Typography>

      {unavailable && <Alert severity="info" sx={{ mb: 2 }}>{unavailable}</Alert>}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {/* ---------------------------------------------------------------- upload */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: HEAD }}>Upload</Typography>
          <Box sx={{ flex: 1 }} />
          {iAmMaster ? (
            <TextField select size="small" label="Upload as" value={uploadScope}
              onChange={(e) => setUploadScope(e.target.value)} sx={{ minWidth: 260 }}>
              <MenuItem value="client">CLIENT — locked to {effectiveTenant || 'this tenant'}</MenuItem>
              <MenuItem value="base">BASE — Optimum-owned, reusable across clients</MenuItem>
            </TextField>
          ) : (
            <Tooltip title="Only Optimum master users can publish BASE documents, because BASE material is visible to every client engagement. Your uploads stay inside your own tenant.">
              <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />} label="Uploads as CLIENT scope"
                sx={{ height: 24, bgcolor: '#E3E8ED', color: GREY, fontWeight: 600 }} />
            </Tooltip>
          )}
        </Stack>

        <Box
          onClick={() => fileRef.current && fileRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          sx={{
            border: '2px dashed', borderColor: dragging ? BLUE : '#cfd8dc', borderRadius: 2,
            p: 5, textAlign: 'center', cursor: 'pointer',
            bgcolor: dragging ? '#f7fbff' : 'transparent',
            '&:hover': { borderColor: BLUE, bgcolor: '#f7fbff' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 44, color: BLUE }} />
          <Typography sx={{ fontWeight: 600, mt: 1 }}>Drag files here, or click to choose</Typography>
          <Typography variant="body2" sx={{ color: GREY }}>
            Files upload straight from your browser to encrypted, tenant-partitioned storage using a
            short-lived presigned URL — the bytes never touch an intermediate server or an inbox.
          </Typography>
          <input ref={fileRef} type="file" multiple hidden
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
        </Box>

        {!!uploads.length && (
          <Box sx={{ mt: 2 }}>
            {uploads.map((u) => (
              <Box key={u.key} sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{u.name}</Typography>
                  <Typography variant="caption" sx={{ color: u.status === 'error' ? '#b3261e' : GREY }}>
                    {u.status === 'error' ? u.error
                      : u.status === 'done' ? 'stored'
                        : u.status === 'presigning' ? 'requesting secure URL…'
                          : u.status === 'registering' ? 'registering…'
                            : `${u.pct}%`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant={u.status === 'uploading' ? 'determinate' : (u.status === 'done' || u.status === 'error' ? 'determinate' : 'indeterminate')}
                  value={u.status === 'error' ? 100 : u.pct}
                  color={u.status === 'error' ? 'error' : 'primary'}
                  sx={{ height: 5, borderRadius: 3 }}
                />
              </Box>
            ))}
            {!activeUploads.length && (
              <Button size="small" onClick={() => setUploads([])} sx={{ color: GREY }}>Clear list</Button>
            )}
          </Box>
        )}
      </Paper>

      {/* --------------------------------------------------------------- filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}>
        <TextField size="small" placeholder="Search title, uploader…" value={text}
          onChange={(e) => setText(e.target.value)} sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: GREY }} /></InputAdornment> }} />
        <TextField select size="small" label="Scope" value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">All scopes</MenuItem>
          <MenuItem value="base">BASE only</MenuItem>
          <MenuItem value="client">CLIENT only</MenuItem>
        </TextField>
        <TextField select size="small" label="Source" value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="all">All sources</MenuItem>
          <MenuItem value="s3">S3 upload</MenuItem>
          <MenuItem value="sharepoint">SharePoint</MenuItem>
          <MenuItem value="confluence">Confluence</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip variant="outlined" label={`${visible.length} of ${files.length} documents`} sx={{ alignSelf: 'center' }} />
      </Stack>

      {/* ----------------------------------------------------------------- table */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD } }}>
                <TableCell>Title</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Uploaded by</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((f) => (
                <TableRow key={f.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{f.title}</TableCell>
                  <TableCell><ScopeChip scope={f.scope} /></TableCell>
                  <TableCell><SourceChip source={f.source} /></TableCell>
                  <TableCell align="right" sx={{ color: GREY }}>
                    {f.source === 's3' ? fmtSize(f.sizeBytes || f.size_bytes) : 'linked'}
                  </TableCell>
                  <TableCell sx={{ color: GREY }}>{f.addedBy || f.added_by || '—'}</TableCell>
                  <TableCell sx={{ color: GREY }}>{fmtDate(f.addedAt || f.added_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={f.source === 's3' ? 'Download (short-lived secure link)' : 'Open in the source system'}>
                      <IconButton size="small" onClick={() => download(f)}>
                        {f.source === 's3' ? <DownloadIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from the exchange">
                      <IconButton size="small" onClick={() => remove(f)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: '#9AA6B2', textAlign: 'center', py: 4 }}>
                    {files.length ? 'No documents match the current filters.' : 'No documents yet — drag a file above or link one from SharePoint or Confluence.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ------------------------------------------------------------ link dialog */}
      <Dialog open={!!linkDialog} onClose={() => !linkBusy && setLinkDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>Link a document</DialogTitle>
        {linkDialog && (
          <DialogContent dividers>
            <Alert severity="info" icon={<LinkIcon fontSize="small" />} sx={{ mb: 2 }}>
              <b>Linked, not copied.</b> Primordia stores the title and the URL only. The client&rsquo;s
              own SharePoint or Confluence stays the source of truth and keeps its permissions, so
              nothing is duplicated, nothing goes stale, and nobody loses access control over it.
            </Alert>

            <TextField select fullWidth size="small" label="System" sx={{ mb: 2 }} value={linkDialog.source}
              onChange={(e) => setLinkDialog({ ...linkDialog, source: e.target.value })}>
              <MenuItem value="sharepoint">SharePoint</MenuItem>
              <MenuItem value="confluence">Confluence</MenuItem>
            </TextField>

            <TextField fullWidth size="small" label="Title" sx={{ mb: 2 }} value={linkDialog.title}
              onChange={(e) => setLinkDialog({ ...linkDialog, title: e.target.value })} />

            <TextField fullWidth size="small" label="URL" sx={{ mb: 2 }} value={linkDialog.url}
              placeholder="https://contoso.sharepoint.com/sites/…"
              onChange={(e) => setLinkDialog({ ...linkDialog, url: e.target.value })} />

            <Divider sx={{ mb: 2 }} />

            {iAmMaster ? (
              <TextField select fullWidth size="small" label="Scope" value={linkDialog.scope}
                onChange={(e) => setLinkDialog({ ...linkDialog, scope: e.target.value })}
                helperText="BASE links are visible in every client engagement — use it only for Optimum-owned reference material.">
                <MenuItem value="client">CLIENT — locked to {effectiveTenant || 'this tenant'}</MenuItem>
                <MenuItem value="base">BASE — Optimum-owned, reusable across clients</MenuItem>
              </TextField>
            ) : (
              <Alert severity="info" icon={<LockIcon fontSize="small" />}>
                Saved with <b>CLIENT</b> scope. Only Optimum master users can publish BASE documents,
                because BASE material is shared across every client engagement.
              </Alert>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setLinkDialog(null)} disabled={linkBusy}>Cancel</Button>
          <Button variant="contained" onClick={saveLink}
            disabled={linkBusy || !linkDialog || !linkDialog.title.trim() || !linkDialog.url.trim()}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}>
            {linkBusy ? 'Saving…' : 'Save link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
